import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import Loader from '../General/Loader';
import BackButton from '../../components/BackButton';

const SimpleAdEnquiry = () => {
  const [adRequests, setAdRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    fetchAdRequests();
  }, [filterStatus]);

  const fetchAdRequests = async () => {
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/adRequests/simple${filterStatus ? `?status=${filterStatus}` : ''}`;
      const response = await axios.get(url);
      const processedData = response.data.adRequests.map(request => ({
        id: request._id,
        content: request.content,
        purpose: request.purpose,
        date: new Date(request.date).toLocaleDateString(),
        contactPerson: request.contactPerson,
        contactNumber: request.contactNumber,
        status: request.status,
        remark: request.remark,
        customerName: request.customerInfo?.customerName || 'N/A',
        customerMobile: request.customerInfo?.customerMobile || 'N/A',
      }));
      setAdRequests(processedData);
    } catch (error) {
      showSnackbar('Error fetching ad requests', 'error');
    }
    setLoading(false);
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API_BASE_URL}/adRequests/${requestId}/status`, {
        status: newStatus
      });
      showSnackbar(`Request ${newStatus} successfully`, 'success');
      fetchAdRequests();
    } catch (error) {
      showSnackbar(`Error updating status: ${error.response?.data?.message || 'Unknown error'}`, 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getStatusButtons = (params) => {
    const { status, id } = params.row;
    
    switch (status) {
      case 'created':
        return (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleStatusChange(id, 'verified')}
            >
              Verify
            </Button>
          </Box>
        );
      case 'verified':
        return (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handleStatusChange(id, 'accepted')}
            >
              Accept
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => handleStatusChange(id, 'rejected')}
            >
              Reject
            </Button>
          </Box>
        );
      case 'accepted':
        return (
          <Typography color="success.main">Accepted</Typography>
        );
      case 'rejected':
        return (
          <Typography color="error.main">Rejected</Typography>
        );
      default:
        return null;
    }
  };

  const columns = [
    { field: 'customerName', headerName: 'Customer Name', width: 150 },
    { field: 'customerMobile', headerName: 'Customer Mobile', width: 130 },
    {
      field: 'content',
      headerName: 'Content',
      width: 200,
      flex: 1,
      renderCell: (params) => {
        const val = params.value;
        if (!val) return 'N/A';
        if (typeof val === 'string' && (val.startsWith('data:') || val.startsWith('http') || val.startsWith('/9j/') || val.startsWith('iVBORw0K') || val.startsWith('<svg'))) {
          const src = val.startsWith('<svg') ? `data:image/svg+xml;utf8,${encodeURIComponent(val)}` : val;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {src.startsWith('data:image') && (
                <img src={src} alt="Ad Thumbnail" style={{ width: 40, height: 30, objectFit: 'cover', borderRadius: 4 }} />
              )}
              <Typography variant="body2" color="primary" fontWeight="bold">
                Media Content
              </Typography>
            </Box>
          );
        }
        return val;
      }
    },
    { field: 'purpose', headerName: 'Purpose', width: 150 },
    { field: 'date', headerName: 'Date', width: 110 },
    { field: 'contactPerson', headerName: 'Contact Person', width: 130 },
    { field: 'contactNumber', headerName: 'Contact Number', width: 130 },
    { field: 'status', headerName: 'Status', width: 100 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: getStatusButtons,
    },
  ];

  return (
     <Box sx={{ position: 'relative', padding: 2 }}>
                  {/* Place BackButton outside the Grid */}
                  <BackButton />
                  <br/>  <br/>
    <Box sx={{ height: 'auto', width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Simple Ad Enquiries
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="created">Created</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="accepted">Accepted</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Loader />
      ) : (
        <DataGrid
          rows={adRequests}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          autoHeight
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              padding: '8px',
            },
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SimpleAdEnquiry;